import './SuggestEditForm.css';

import React, { ChangeEvent, FormEvent } from 'react';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import { IVenue, VenueTags, SubmissionType } from 'btdt-types';
import { FaPaperPlane } from 'react-icons/fa';
import { TagLabels } from '../consts';
import { useCreateSubmissionMutation } from '../hooks/useSubmission';
import { useRecaptchaToken } from '../hooks/useRecaptcha';

interface IProps {
  venue: IVenue;
  onDone(): void;
}

function SuggestEditForm({ venue, onDone }: IProps): React.ReactElement {
  const [submitterName, setSubmitterName] = React.useState(sessionStorage.getItem('userName') || '');
  const [submitterEmail, setSubmitterEmail] = React.useState(sessionStorage.getItem('userEmail') || '');
  const [name, setName] = React.useState(venue.name || '');
  const [nameEn, setNameEn] = React.useState(venue.nameEn || '');
  const [address, setAddress] = React.useState(venue.address || '');
  const [yearsCertain, setYearsCertain] = React.useState('');
  const [yearOpened, setYearOpened] = React.useState(venue.yearOpened ? String(venue.yearOpened) : '');
  const [yearClosed, setYearClosed] = React.useState(venue.yearClosed ? String(venue.yearClosed) : '');
  const [tags, setTags] = React.useState<VenueTags[]>(venue.tags || []);
  const [source, setSource] = React.useState('');
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [photoCredits, setPhotoCredits] = React.useState<string[]>([]);
  const [photoCaptions, setPhotoCaptions] = React.useState<string[]>([]);
  const [copyrightConfirmed, setCopyrightConfirmed] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const getRecaptchaToken = useRecaptchaToken();

  const { isLoading: isSubmitting, mutate } = useCreateSubmissionMutation({
    onSuccess: () => setSubmitted(true),
  });

  const handleTagToggle = (tag: VenueTags) => {
    setTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  };

  const handlePhotosChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setPhotos((prev) => [...prev, ...files]);
    setPhotoCredits((prev) => [...prev, ...files.map(() => '')]);
    setPhotoCaptions((prev) => [...prev, ...files.map(() => '')]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoCredits((prev) => prev.filter((_, i) => i !== index));
    setPhotoCaptions((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePhotoCredit = (index: number, credit: string) => {
    setPhotoCredits((prev) => prev.map((c, i) => i === index ? credit : c));
  };

  const updatePhotoCaption = (index: number, caption: string) => {
    setPhotoCaptions((prev) => prev.map((c, i) => i === index ? caption : c));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (photos.length > 0 && !copyrightConfirmed) {
      alert('יש לאשר את תנאי השימוש בתמונות');
      return;
    }
    sessionStorage.setItem('userName', submitterName);
    sessionStorage.setItem('userEmail', submitterEmail);

    let recaptchaToken: string | undefined;
    try {
      recaptchaToken = await getRecaptchaToken();
    } catch (error) {
      console.error('reCAPTCHA token failed:', error);
    }

    mutate({
      type: SubmissionType.SUGGEST_EDIT,
      venueId: venue.id,
      venueData: JSON.stringify({
        name, nameEn, address, yearsCertain,
        yearOpened: yearOpened ? parseInt(yearOpened) : undefined,
        yearClosed: yearClosed ? parseInt(yearClosed) : undefined,
        tags,
      }),
      source,
      submitterName,
      submitterEmail,
      photos,
      photoCredits,
      photoCaptions,
      recaptchaToken,
    });
  };

  if (submitted) {
    return (
      <div className="SuggestEditForm">
        <div className="SuggestEditForm-success">
          <p>תודה! ההצעה התקבלה ותיבדק על ידי הצוות שלנו.</p>
          <Button onClick={onDone} size="sm" sx={{ marginTop: '12px' }}>סגירה</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="SuggestEditForm">
      <h2>הצעת תיקון</h2>
      <form onSubmit={handleSubmit}>
        <div className="SuggestEditForm-grid">
          <div>
            <label>שם</label>
            <Input
              value={submitterName}
              onChange={(e) => setSubmitterName(e.target.value)}
              size="sm"
              autoComplete="name"
            />
          </div>

          <div>
            <label>דואר אלקטרוני</label>
            <div dir={submitterEmail ? 'ltr' : 'rtl'}>
              <Input
                type="email"
                value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                size="sm"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label>שם המקום</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} size="sm" />
          </div>

          <div>
            <label>שם באנגלית</label>
            <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} size="sm" dir="ltr" />
          </div>

          <div className="full-width">
            <label>כתובת</label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} size="sm" />
          </div>

          <div className="full-width">
            <label>שנים שבטוח שהמקום היה פעיל</label>
            <Input
              value={yearsCertain}
              onChange={(e) => setYearsCertain(e.target.value)}
              size="sm"
              placeholder="למשל: 1985-1992"
            />
          </div>

          <div>
            <label>שנת פתיחה</label>
            <Input
              type="number"
              value={yearOpened}
              onChange={(e) => setYearOpened(e.target.value)}
              size="sm"
            />
          </div>

          <div>
            <label>שנת סגירה</label>
            <Input
              type="number"
              value={yearClosed}
              onChange={(e) => setYearClosed(e.target.value)}
              size="sm"
            />
          </div>

          <div className="full-width">
            <label>קטגוריות</label>
            <div className="SuggestEditForm-tags">
              {Object.values(VenueTags).map((tag) => (
                <label key={tag}>
                  <input
                    type="checkbox"
                    checked={tags.includes(tag)}
                    onChange={() => handleTagToggle(tag)}
                  />
                  {TagLabels[tag]}
                </label>
              ))}
            </div>
          </div>

          <div className="full-width">
            <label>מקור המידע</label>
            <Input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              size="sm"
              placeholder="לינק לכתבה, אתר, פוסט או מקור אחר שיעזור לנו לאמת את המידע"
            />
          </div>

          <div className="full-width">
            <label>הוספת תמונות</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotosChange}
              style={{ margin: '4px 0' }}
            />
            {photos.map((file, index) => (
              <div key={index} className="SuggestEditForm-photo-item">
                <img src={URL.createObjectURL(file)} alt="" />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Input
                    value={photoCaptions[index]}
                    onChange={(e) => updatePhotoCaption(index, e.target.value)}
                    size="sm"
                    placeholder="תיאור התמונה"
                  />
                  <Input
                    value={photoCredits[index]}
                    onChange={(e) => updatePhotoCredit(index, e.target.value)}
                    size="sm"
                    placeholder="קרדיט לצילום"
                  />
                </div>
                <button type="button" onClick={() => removePhoto(index)}>✕</button>
              </div>
            ))}

            {photos.length > 0 && (
              <div className="SuggestEditForm-copyright">
                <label>
                  <input
                    type="checkbox"
                    checked={copyrightConfirmed}
                    onChange={(e) => setCopyrightConfirmed(e.target.checked)}
                  />
                  התמונה מאושרת לשימוש ללא מטרות רווח
                </label>
              </div>
            )}
          </div>
        </div>

        <Button
          type="submit"
          startDecorator={<FaPaperPlane />}
          disabled={isSubmitting}
          sx={{ marginTop: '12px' }}
        >
          שליחה
        </Button>
        <p style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
          אתר זה מוגן באמצעות reCAPTCHA של Google.
          {' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: '#aaa' }}>מדיניות פרטיות</a>
          {' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: '#aaa' }}>תנאי שימוש</a>
        </p>
      </form>
    </div>
  );
}

export default SuggestEditForm;
